import { useEffect } from "react";

import { useParams } from "react-router-dom";

import {
  getSearchFeaturedMention,
  getSearchList,
} from "@/api/endpoints/searches";
import {
  activate,
  ActivationData,
  validateActivationKey,
} from "@/api/endpoints/user";
import { ErrorMessages } from "@/api/fetcher_v2";
import FormCard from "@/containers/Auth/FormCard";
import { ROUTES } from "@/routes_new";
import { User } from "@/types/User";
import ActivateForm from "@/views/ActivationView/partials/Activate/ActivateForm";
import { useOnboardingContext } from "@/views/ActivationView/partials/OnboardingContext";
import Typography from "@components/Typography";
import { useWizard } from "@components/Wizard";
import { i18n } from "@lingui/core";

import styles from "./Activate.module.css";

const redirect = () => (window.location.href = ROUTES.LOGIN);

const mergeNameErrors = (errors: ErrorMessages<ActivationData>) => {
  if (errors.first_name || errors.last_name) {
    return {
      ...errors,
      fullname: [errors.last_name, errors.first_name]
        .filter(Boolean)
        .join(", "),
    };
  }
  return errors;
};

const Activate: React.FunctionComponent = () => {
  const { activation_key } = useParams<{ activation_key: string }>();
  const { next } = useWizard();
  const { setUser, user, setFeaturedMention, setMissingFeatured } =
    useOnboardingContext();

  useEffect(() => {
    if (activation_key) {
      validateActivationKey(activation_key).then((res) => {
        // redirect if activation_key is invalid
        if (res.status !== 200) {
          redirect();
        }
        if (res.ok && res.data) {
          setUser({
            email: res.data.email,
            locale: res.data.language,
          } as unknown as User);
          i18n.activate(res.data.language);
        }
      });
    } else {
      // redirect if no token was supplied
      redirect();
    }
  }, [activation_key, setUser]);

  const send = async (values: ActivationData) => {
    const activationRes = await activate(values);

    // Set the user received from the activation
    if (activationRes.ok && activationRes.data) {
      setUser(activationRes.data);
    } else {
      if (activationRes.data) {
        return mergeNameErrors(activationRes.data);
      }
    }
    // Go to the next step of the flow, awaiting the API results below
    next();

    // The user is now authenticated and should be able to get their searches
    try {
      const listResponse = await getSearchList({ light: true });
      const firstSearch = listResponse.ok && listResponse.data?.[0];

      if (firstSearch) {
        const featuredRes = await getSearchFeaturedMention(firstSearch.uuid);
        if (featuredRes.ok && featuredRes.data) {
          setFeaturedMention(featuredRes.data);
        } else {
          setMissingFeatured(true);
        }
      } else {
        setMissingFeatured(true);
      }
    } catch {
      setMissingFeatured(true);
    }

    return activationRes;
  };

  return (
    <div className={styles.root} data-testid="activation-card">
      <FormCard>
        <Typography
          variant="h1"
          align="center"
          data-testid="activation-card-title"
        >
          {L.ACTIVATE_TITLE}
        </Typography>
        <ActivateForm
          initialValues={{ activation_key, email: user?.email }}
          onSubmit={send}
        />
      </FormCard>
    </div>
  );
};

export default Activate;
